import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CalendarClock, RefreshCw } from "lucide-react";
import CancelRenewalDialog from "../components/CancelRenewalDialog";
import AutopayPanel from "../components/AutopayPanel";

export const STATE_STYLE = {
  active: ["Active", "bg-green-100 text-green-800"],
  grace: ["Grace period", "bg-amber-100 text-amber-800"],
  expired: ["Expired", "bg-red-50 text-red-800"],
  ended: ["Ended", "bg-red-50 text-red-800"],
};

export const startRenewal = async (token, setBusy) => {
  setBusy(true);
  try {
    const { data } = await api.post(`/renewals/${token}/renew`, { origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  } catch (e) { toast.error(formatError(e)); setBusy(false); }
};

export default function Renew() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/renewals/${token}`).then((r) => {
      setPlan(r.data);
      if (params.get("go") === "1") startRenewal(token, setBusy);
    }).catch((e) => setError(formatError(e)));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const resume = async () => {
    try { setPlan((await api.post(`/renewals/${token}/resume`)).data); toast.success("Renewal reminders are back on"); }
    catch (e) { toast.error(formatError(e)); }
  };

  if (error) return <div data-testid="renew-error" className="max-w-xl mx-auto px-4 py-24 text-center text-red-700">{error}</div>;
  if (!plan) return <div className="p-10 text-center">Loading…</div>;
  const [label, cls] = STATE_STYLE[plan.state];
  const canCancel = plan.auto_renew && ["active", "grace"].includes(plan.state);

  return (
    <div data-testid="renew-page" className="max-w-xl mx-auto px-4 py-20">
      <div className="font-devanagari text-5xl text-[#D4AF37] text-center">ॐ</div>
      <div className="overline text-center mt-4">Namaste {plan.first_name}</div>
      <div className="mt-6 bg-[#FFFDF9] sacred-border rounded-3xl p-8">
        <div className="flex items-start justify-between gap-4">
          <h1 data-testid="renew-plan-name" className="font-serif text-3xl text-[#4A0E17] leading-tight">{plan.plan_name}</h1>
          <span data-testid="renew-state" className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold ${cls}`}>{label}</span>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
          <div><div className="overline !text-[0.6rem]">{plan.state === "active" || plan.state === "grace" ? "Ends on" : "Ended on"}</div>
            <div data-testid="renew-expiry" className="font-mono text-lg text-[#4A0E17] flex items-center gap-2 mt-1"><CalendarClock className="w-4 h-4 text-[#E65100]" />{plan.expiry || "—"}</div></div>
          <div><div className="overline !text-[0.6rem]">Renewal price</div>
            <div data-testid="renew-price" className="font-mono text-lg text-[#E65100] mt-1">${plan.price_usd.toFixed(2)} <span className="text-xs text-[#63534E]">/ {plan.period}</span></div></div>
        </div>
        {plan.state === "grace" && <p data-testid="renew-grace-note" className="mt-5 text-sm bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-900">Your plan has ended, but we're keeping it on for {plan.grace_days} days. Renew now to keep everything as it is.</p>}
        {plan.cancel_mode === "period_end" && plan.state === "active" && <p data-testid="renew-cancelled-note" className="mt-5 text-sm text-[#63534E]">Renewal is cancelled. Your access stays on until {plan.expiry}, and you won't get any more reminders.</p>}
        <p className="mt-5 text-xs text-[#8C6D3B]">Renewing adds a full {plan.period} from your current end date, so you never lose days.</p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Button data-testid="renew-now-btn" disabled={busy} onClick={() => startRenewal(token, setBusy)} className="btn-saffron rounded-full h-11 px-6">
            <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} />{busy ? "Opening checkout…" : `Renew now · $${plan.price_usd.toFixed(2)}`}
          </Button>
          {canCancel && <CancelRenewalDialog token={token} expiry={plan.expiry} onDone={setPlan} />}
          {plan.cancel_mode === "period_end" && ["active", "grace"].includes(plan.state) && <Button data-testid="renew-resume-btn" variant="outline" onClick={resume} className="rounded-full h-11">Turn reminders back on</Button>}
        </div>
        <AutopayPanel plan={plan} token={token} onChange={setPlan} />
        {plan.last_ecard_id && <Link to={`/ecard/${plan.last_ecard_id}`} data-testid="renew-ecard-link" className="mt-5 inline-block text-sm text-[#E65100] font-semibold">Open your thank-you festival e-card →</Link>}
      </div>
      <div className="text-center mt-8"><Link to="/" className="text-sm text-[#E65100] font-semibold">← Back to Pancham Dham</Link></div>
    </div>
  );
}
